// @ts-strict-ignore
import React, { ReactNode } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Box, Button, RowsSkeletonLoader } from '@node-space/ui-components'
import { ListItemAnimation } from 'components/Animations/ListItemAnimation'
import { SkeletonListItemAnimation } from 'components/Animations/SkeletonListItemAnimation'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useContactList } from './useContactList'

interface ContactOrPaymentInstrumentListProps<T> {
  list: T[] | undefined
  isFetchingNextPage: boolean
  hasLoadedMore: boolean
  hasNextPage: boolean
  setHasLoadedMore: React.Dispatch<React.SetStateAction<boolean>>
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<T[], Error>>
  getItemKey: (item: T) => string
  listItemRenderer: (item: T, index: number) => ReactNode
  footerRenderer?: (handleOnLoadMore: () => void) => ReactNode
}

export const ContactOrPaymentInstrumentList = <T extends object>({
  list,
  isFetchingNextPage,
  hasLoadedMore,
  hasNextPage,
  setHasLoadedMore,
  fetchNextPage,
  getItemKey,
  listItemRenderer,
  footerRenderer,
}: ContactOrPaymentInstrumentListProps<T>) => {
  const { t } = useTranslation()

  const { listRef, contactList, handleOnLoadMore } = useContactList({
    contacts: list,
    isFetchingNextPage,
    hasLoadedMore,
    fetchNextPage,
    setHasLoadedMore,
  })

  return (
    <motion.ul ref={listRef} className="h-[calc(80vh-244px)] overflow-y-auto">
      <AnimatePresence>
        {contactList?.map((item, index) => {
          if ('loading' in item) {
            return (
              <SkeletonListItemAnimation key={item.id} reference={item.id}>
                <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
              </SkeletonListItemAnimation>
            )
          }

          const key = getItemKey(item)
          const isLastTwo = contactList.length - index <= 2
          const isNextToAnimate = contactList.length - index > 2 && contactList.length - index <= 10

          return (
            <ListItemAnimation
              key={key}
              reference={key}
              index={index}
              listLength={contactList.length}
              hasLoadedMore={hasLoadedMore}
              isLastTwo={isLastTwo}
              isNextToAnimate={isNextToAnimate}
            >
              {listItemRenderer(item, index)}
            </ListItemAnimation>
          )
        })}

        <Box flex justify="center" className="mt-2">
          {footerRenderer ? (
            footerRenderer(handleOnLoadMore)
          ) : (
            <Button
              onClick={handleOnLoadMore}
              variant="ghost"
              disabled={!hasNextPage || isFetchingNextPage}
            >
              {hasNextPage ? t('contacts.loadMore') : t('contacts.noMoreContacts')}
            </Button>
          )}
        </Box>
      </AnimatePresence>
    </motion.ul>
  )
}
